import React from 'react';
import { Row, Col, Tooltip } from 'antd';
import { I18n } from '@aws-amplify/core';
import { InfoCircleOutlined } from '@ant-design/icons';

import { useAutoCalculate } from './hooks';
import { getDecimalSeparator } from '@/utils/getDecimalSeparator';
import { FieldNamesEnum } from '@/components/views/Report/KimLhc/types';
import { PartialResultBadge, VideoDrawerHeader, ResultBadge } from '@/components/views/Report/KimLhc/Components/Shared';
import {
	LoadWeightFormItem,
	LoadInclinationFormItem,
	MovementFrequencyFormItem,
	LoadTransportInPairsFormItem,
	LoadHandlingConditionsFormItem
} from './Fields';

import { SectionTitle, SectionDescription } from '@/components/views/Report/KimLhc/Components/Shared/styles';
import * as S from './styles';

export function Frequency(): JSX.Element {
	const FREQUENCY_GROUP = FieldNamesEnum.FREQUENCY;

	const { frequencyScore, effectiveWeight, manWeightScore, womanWeightScore, cargoHandlingConditionScore } =
		useAutoCalculate();

	return (
		<Row gutter={[0, 40]}>
			<Col span={24}>
				<VideoDrawerHeader title={I18n.get('Frequency and load')} />
			</Col>
			<Col span={24}>
				<Row gutter={[0, 48]}>
					<Col span={24}>
						<Row gutter={[0, 16]}>
							<Col span={24}>
								<Row gutter={16} align="middle" justify="space-between">
									<Col flex="1">
										<SectionTitle level={5}>{I18n.get('Movement frequency')}</SectionTitle>
										<SectionDescription>
											{I18n.get(
												'Select the maximum number of times the movement is performed per activity and workday'
											)}
										</SectionDescription>
									</Col>
									<Col>
										<ResultBadge value={frequencyScore} />
									</Col>
								</Row>
							</Col>
							<Col span={24}>
								<S.SliderContainer>
									<MovementFrequencyFormItem fieldName={[FREQUENCY_GROUP, 'frequency']} />
								</S.SliderContainer>
							</Col>
						</Row>
					</Col>
					<Col span={24}>
						<Row gutter={[60, 24]}>
							<Col span={24}>
								<Row gutter={16} align="middle" justify="space-between">
									<Col flex="1">
										<SectionTitle level={5}>
											{I18n.get('Load weight')}
											<S.RequiredAsterisk>*</S.RequiredAsterisk>
										</SectionTitle>
									</Col>
									<Col>
										<S.ScoreGroup>
											<S.LabelText>{I18n.get('Men:')}</S.LabelText>
											<ResultBadge value={manWeightScore} />
										</S.ScoreGroup>
									</Col>
									<Col>
										<S.ScoreGroup>
											<S.LabelText>{I18n.get('Women:')}</S.LabelText>
											<ResultBadge value={womanWeightScore} />
										</S.ScoreGroup>
									</Col>
								</Row>
							</Col>
							<Col span={24}>
								<S.WeightRow>
									<S.WeightInputWrapper>
										<LoadWeightFormItem fieldName={[FREQUENCY_GROUP, 'weight']} />
									</S.WeightInputWrapper>

									<S.CheckboxesWrapper>
										<Row gutter={[0, 24]} align="middle">
											<Col>
												<LoadTransportInPairsFormItem
													fieldName={[FREQUENCY_GROUP, 'transporting_cargo_in_pairs']}
												/>
											</Col>
											<Col>
												<S.DividerVertical />
											</Col>
											<Col>
												<LoadInclinationFormItem
													fieldName={[FREQUENCY_GROUP, 'inclined_weight']}
												/>
											</Col>
										</Row>
									</S.CheckboxesWrapper>
								</S.WeightRow>
							</Col>
							<Col span={24}>
								<S.EffectiveLoadContainer>
									<S.EffectiveLoadLabel>{I18n.get('Effective load weight')}:</S.EffectiveLoadLabel>
									<Tooltip title={I18n.get('Effective load weight tooltip')}>
										<S.InfoIconWrapper>
											<InfoCircleOutlined />
										</S.InfoIconWrapper>
									</Tooltip>
									<S.EffectiveLoadValue>
										<span>
											{effectiveWeight.toFixed(2).replace('.', getDecimalSeparator() ?? '.')} kg
										</span>
									</S.EffectiveLoadValue>
								</S.EffectiveLoadContainer>
							</Col>
						</Row>
					</Col>
					<Col span={24}>
						<Row gutter={[16, 16]}>
							<Col span={24}>
								<Row gutter={24} align="middle" justify="space-between">
									<Col flex="1">
										<SectionTitle level={5}>
											{I18n.get('Load handling conditions')}
											<S.RequiredAsterisk>*</S.RequiredAsterisk>
										</SectionTitle>
									</Col>
									<Col>
										<ResultBadge value={cargoHandlingConditionScore} />
									</Col>
								</Row>
							</Col>
							<Col span={24}>
								<LoadHandlingConditionsFormItem
									fieldName={[FREQUENCY_GROUP, 'cargo_handling_condition']}
								/>
							</Col>
						</Row>
					</Col>
					<PartialResultBadge />
				</Row>
			</Col>
		</Row>
	);
}
